import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GhostTrend } from "@/components/Ghost";
import { FadeUp, Eyebrow } from "@/lib/motion";
import { CASES, ALL_CASES, CASE_STATS } from "@/data/caseStudies";
import CaseLogo from "@/components/CaseLogo";
import CaseStatChips from "@/components/CaseStatChips";

export default function CaseStudies() {
  return (
    <section
      id="case-studies"
      data-testid="case-studies-section"
      className="relative overflow-hidden border-t border-slate-200 bg-gradient-to-b from-white via-[#F8F9FA] to-[#F1F3F5] py-24 md:py-32"
    >
      <GhostTrend className="-right-24 top-10 text-flame/[0.1]" />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <FadeUp>
          <Eyebrow>Case studies</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-display text-4xl font-bold leading-[1.02] tracking-tight text-slate-900 sm:text-5xl">
            Real clients. <span className="text-flame">Real turnarounds.</span>
          </h2>
          <p className="mt-5 max-w-xl text-lg leading-relaxed text-mist md:text-xl">
            Every result below comes straight from the client in their own
            words — no rounding up, no creative accounting.
          </p>
        </FadeUp>

        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {CASES.map((c, i) => (
            <FadeUp key={c.client} delay={(i % 2) * 0.08}>
              <Link
                to={`/case-studies/${c.slug}`}
                data-testid={`case-study-link-${c.num}`}
                className="block h-full"
              >
                <article
                  data-testid={`case-study-row-${c.num}`}
                  className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-flame/40 hover:shadow-xl"
                >
                  <div className="flex items-start justify-between gap-4">
                    <CaseLogo slug={c.slug} client={c.client} />
                    <span className="font-mono text-base text-faint transition-colors duration-300 group-hover:text-flame">
                      {c.num}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                    {c.client}
                  </h3>
                  <div className="mt-2 font-mono text-[13px] uppercase tracking-[0.2em] text-faint">
                    {c.industry}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {c.tags.map((t) => (
                      <span
                        key={t}
                        className="rounded-full border border-slate-200 bg-coal/70 px-3 py-1 font-mono text-[13px] uppercase tracking-[0.12em] text-mist"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <div className="mt-5 grid gap-4 border-t border-slate-100 pt-5 sm:grid-cols-2">
                    <div>
                      <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-faint">
                        Before
                      </div>
                      <p className="mt-2 text-base leading-relaxed text-mist">
                        {c.before}
                      </p>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.25em] text-flame">
                        After
                        <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                      </div>
                      <p className="mt-2 text-base leading-relaxed text-slate-700">
                        {c.after}
                      </p>
                    </div>
                  </div>

                <CaseStatChips
                  items={CASE_STATS[c.slug]}
                  testPrefix={`case-stat-${c.slug}`}
                  className="mt-auto pt-6"
                />
                <div className="mt-6 flex items-center gap-2 font-mono text-[13px] uppercase tracking-[0.2em] text-flame">
                  Read the full story
                  <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                </div>
              </article>
              </Link>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-12 flex flex-wrap justify-center gap-4">
            <Link
              to="/case-studies"
              data-testid="case-studies-index-link"
              className="group flex items-center gap-2 rounded-2xl bg-flame px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-obsidian transition-all duration-300 hover:shadow-[0_0_28px_rgba(255,85,0,0.45)]"
            >
              {ALL_CASES.length} case studies — explore them all
              <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/testimonials"
              data-testid="testimonials-page-link"
              className="group flex items-center gap-2 rounded-2xl border border-slate-300 px-6 py-3 font-mono text-base font-semibold uppercase tracking-[0.15em] text-slate-900 transition-all duration-300 hover:border-flame/60 hover:text-flame"
            >
              See all client testimonials
              <ArrowRight className="h-3.5 w-3.5 text-flame transition-transform duration-300 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
